/**
 * Author: Aldrin
 * Description: {Handles} dependency-based navigation flow between quality modules.
 * Created On: 2025-Nov-20
 */

import { Component, ViewChild, TemplateRef, ElementRef, Renderer2, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { AppService } from 'src/app/app.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaginationService } from 'src/app/PaginationService';
import { messageService } from 'src/app/message.service';
import { qmsService } from '../qms.service';
import { DatepickerDateCustomClasses, BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { GridReorderComponent } from 'src/app/grid-reorder/grid-reorder.component';
import { AuService } from '../../qms/au/au.service';
import { EditorComponent } from 'src/app/editor/editor.component';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { MatSelect } from '@angular/material/select';
import { SuccessSnackbarComponent } from 'src/app/support/success-snackbar/success-snackbar.component';
import * as moment from 'moment-timezone';
import { EditorConfig, ST_BUTTONS } from 'ngx-simple-text-editor';
import { CoreNavigationService } from 'src/app/support/navigation/core-navigation.service';
import { DependencyNavigationService } from 'src/app/support/navigation/dependency-navigation.service';
import { FlexibleFieldsComponent } from '../flexible-fields/flexible-fields.component';
import { ExcelPdfComponent } from 'src/app/support/excel-pdf/excel-pdf.component';
import { DynamicAuditTrailComponent } from 'src/app/support/dynamic-audit-trail/dynamic-audit-trail.component';

type NullableDate = Date | string | null;
const auditStatus = [
  'Plan Cancelled',
  'Plan Deleted',
  'Schedule Cancelled',
  'Schedule Deleted',
  'Schedule locked/New plans not allowed',
  'Re-Draft(Schedule)',
  'Scheduled',
  'Schedule Rejected',
  'Pending Schedule Approval',
  'Draft(Schedule)'
] as const;

type AuditStatus = (typeof auditStatus)[number];

export interface CR {
  shortDescription?: string | null;
  changeDescription?: string;
  changeJustification?: string;
  reasonForChange?: string | null;
  expectedToBeStartedOn?: NullableDate;
  expcetedClosureOn?: NullableDate;
  effectivenessCheckReqd?: boolean | 'Y' | 'N';
  isRiskCalculationReqd?: boolean | 'Y' | 'N';
  deptCode?: string | null;
  owner?: string | null;
  requestedAt?: string | null;
  changeTypeCode?: string | null;
  coreBusinessAreaName?: string | null;
  reasonForChangeCode?: string | null;
}

export const RESET_CR: CR = {
  shortDescription: null,
  changeDescription: '',
  changeJustification: '',
  reasonForChange: null,
  expectedToBeStartedOn: null,
  expcetedClosureOn: null,
  effectivenessCheckReqd: false,
  isRiskCalculationReqd: true,
  deptCode: null,
  owner: null,
  requestedAt: null,
  changeTypeCode: null,
  coreBusinessAreaName: null,
  reasonForChangeCode: null,
};

export interface CAPA {
  shortDescription?: string | null;
  capaDescription?: string;
  reasonForCapa?: string | null;
  expectedToBeStartedOn?: NullableDate;
  expcetedClosureOn?: NullableDate;
  effectivenessCheckReqd?: boolean;
  isRiskCalculationReqd?: boolean;
  deptCode?: string | null;
  owner?: string | null;
  capaType?: string | null;
  requestedAt?: string | null;
  coreBusinessAreaName?: string | null;
  reasonForCapaCode?: string | null;
}

export const RESET_CAPA: CAPA = {
  shortDescription: null,
  capaDescription: '',
  reasonForCapa: null,
  expectedToBeStartedOn: null,
  expcetedClosureOn: null,
  effectivenessCheckReqd: false,
  isRiskCalculationReqd: true,
  deptCode: null,
  owner: null,
  capaType: null,
  requestedAt: null,
  coreBusinessAreaName: null,
  reasonForCapaCode: null,
}

export interface DV {
  shortDescription?: string | null;
  dvDescription?: string;
  reasonForDeviation?: string | null;
  deviationOccuredOn?: NullableDate;
  deviationIdentifiedOn?: NullableDate;
  expectedToBeStartedOn?: NullableDate;
  expcetedClosureOn?: NullableDate;
  effectivenessCheckReqd?: boolean;
  isRiskCalculationReqd?: boolean;
  deptCode?: string | null;
  owner?: string | null;
  deviationType?: string | null;
  deviationCriticality?: string | null;
  requestedAt?: string | null;
  coreBusinessAreaName?: string | null;
  reasonForDeviationCode?: string | null;
}

export const RESET_DV: DV = {
  shortDescription: null,
  dvDescription: '',
  reasonForDeviation: null,
  deviationOccuredOn: null,
  deviationIdentifiedOn: null,
  expectedToBeStartedOn: null,
  expcetedClosureOn: null,
  effectivenessCheckReqd: false,
  isRiskCalculationReqd: true,
  deptCode: null,
  owner: null,
  deviationType: null,
  deviationCriticality: null,
  requestedAt: null,
  coreBusinessAreaName: null,
  reasonForDeviationCode: null,
}

export interface IN {
  shortDescription?: string | null;
  description?: string;
  referenceOthers?: string | null;
  impactDescription?: string;
  identifiedOn?: NullableDate;
  expectedToBeStartedOn?: NullableDate;
  expcetedClosureOn?: NullableDate;
  isRiskCalculationReqd?: boolean;
  initiatedOnBehalfByOthers?: boolean;
  isOutsideOrganization?: boolean;
  inType?: string | null;
  deptCode?: string | null;
  requestedAt?: string | null;
  reference?: string | null;
  initiatedOnBehalf?: string | null;
  impactType?: string | null;
  coreBusinessAreaName?: string | null;
  effectivenessCheckReqd: boolean;
}

export const RESET_IN: IN = {
  shortDescription: null,
  description: '',
  referenceOthers: null,
  impactDescription: '',
  identifiedOn: null,
  expectedToBeStartedOn: null,
  expcetedClosureOn: null,
  isRiskCalculationReqd: true,
  initiatedOnBehalfByOthers: false,
  isOutsideOrganization: false,
  inType: null,
  deptCode: null,
  requestedAt: null,
  reference: null,
  initiatedOnBehalf: null,
  impactType: null,
  coreBusinessAreaName: null,
  effectivenessCheckReqd: false,
}
export interface QI {
  shortDescription?: string | null;
  description?: string;
  referenceOthers?: string | null;
  impactDescription?: string;
  identifiedOn?: NullableDate;
  expectedToBeStartedOn?: NullableDate;
  expcetedClosureOn?: NullableDate;
  effectivenessCheckReqd?: boolean;
  isRiskCalculationReqd?: boolean;
  qiType?: string | null;
  deptCode?: string | null;
  requestedAt?: string | null;
  coreBusinessAreaName?: string | null;
  reference?: string | null;
  impactType?: string | null;
  evaluator?: string | null;
}

export const RESET_QI: QI = {
  shortDescription: null,
  description: '',
  referenceOthers: null,
  impactDescription: '',
  identifiedOn: null,
  expectedToBeStartedOn: null,
  expcetedClosureOn: null,
  effectivenessCheckReqd: false,
  isRiskCalculationReqd: true,
  qiType: null,
  deptCode: null,
  requestedAt: null,
  coreBusinessAreaName: null,
  reference: null,
  impactType: null,
  evaluator: null,
}
export interface CC {
  reporterFirstName?: string | null;
  reporterMiddleName?: string | null;
  reporterLastName?: string | null;
  reporterDesignation?: string | null;
  emailAddress?: string | null;
  reporterFax?: string | null;
  reporterContactNumber?: string | null;
  reporterContactNumberOffice?: string | null;
  reporterAddress1?: string | null;
  reporterAddress2?: string | null;
  reporterCity?: string | null;
  reporterState?: string | null;
  reporterZipcode?: string | null;
  reporterComments?: string | null;
  isRiskCalculationReqd?: boolean;
  reporterTitle?: string | null;
  reporterType?: string | null;
  reporterCountry?: string | null;
}

export const RESET_CC: CC = {
  reporterFirstName: null,
  reporterMiddleName: null,
  reporterLastName: null,
  reporterDesignation: null,
  emailAddress: null,
  reporterFax: null,
  reporterContactNumber: null,
  reporterContactNumberOffice: null,
  reporterAddress1: null,
  reporterAddress2: null,
  reporterCity: null,
  reporterState: null,
  reporterZipcode: null,
  reporterComments: null,
  isRiskCalculationReqd: true,
  reporterTitle: null,
  reporterType: null,
  reporterCountry: null
}
export interface OOS {
  oostType?: string | null;
  initiatingDeptCode?: string | null;
  oosDeptCode?: string | null;
  owner?: number | null;
  dateOfOccurrence?: NullableDate;
  identifiedOn?: NullableDate;
  expectedToBeStartedOn?: NullableDate;
  expcetedClosureOn?: NullableDate;
}

export const RESET_OOS: OOS = {
  oostType: null,
  initiatingDeptCode: null,
  oosDeptCode: null,
  owner: null,
  dateOfOccurrence: null,
  identifiedOn: null,
  expectedToBeStartedOn: null,
  expcetedClosureOn: null,
}
export interface PopupData {
  title: string;
}

export interface ModuleRouteConfig {
  moduleName: string;
  moduleFullName: any,
  API: () => Promise<any>;
  extractGroupId: (data: any) => number;
  extractActivityId: (data: any) => any;
  targetRoute: string;
}

export interface Dependency {
  groupId?: number | null,
  module?: string | null,
  mode?: string;
  showModule?: boolean,
  groupDetail?: any,
  requestedAtName?: string,
  isBusinessAreaConfigured?: boolean,
  isAllBusinessAreaSelected?: boolean,
  isAllReasonSelected?: boolean,
  selectedBusinessArea: [],
  selectedReason: [],
  createdActivityId?: number,
}

export const RESET_DEPENDENCY: Dependency = {
  groupId: null,
  module: null,
  mode: '',
  showModule: false,
  requestedAtName: '',
  isBusinessAreaConfigured: false,
  isAllBusinessAreaSelected: false,
  isAllReasonSelected: false,
  selectedBusinessArea: [],
  selectedReason: [],
  groupDetail: {},
  createdActivityId: 0
}

export const SECONDARY_RESET_DEPENDENCY: Dependency = {
  selectedBusinessArea: [],
  selectedReason: [],
  groupDetail: {},
  module: null,
  showModule: false,
  isBusinessAreaConfigured: false,
  isAllBusinessAreaSelected: false,
  isAllReasonSelected: false,
}

export interface MapDependency {
  module?: string | null,
  selectedModule?: string,
  groupId?: number | null,
  refNo?: number | null,
  department?: string | null,
  role?: string | null,
  owner?: string | null,
  status?: string | null,
  mappingDependency?: any
  dependencies: any[]
}

export const RESET_MAP_DEPENDENCY: MapDependency = {
  module: null,
  selectedModule: '',
  groupId: null,
  refNo: null,
  department: null,
  role: null,
  owner: null,
  status: null,
  mappingDependency: {},
  dependencies: []
}

@Component({
  selector: 'app-quality-dependencies',
  templateUrl: './quality-dependencies.component.html',
  styleUrls: ['./quality-dependencies.component.scss']
})

export class QualityDependenciesComponent implements OnInit, AfterViewInit, OnDestroy {

  dependency: Dependency;
  mapDependency: MapDependency;
  cr: CR;
  capa: CAPA;
  dv: DV;
  incident: IN;
  qi: QI;
  cc: CC;
  oos: OOS;

  isProcessing: boolean = false;
  dateCustomClasses: DatepickerDateCustomClasses[];
  popup: PopupData = { title: '' };
  dateFormat: any = this.message.organizationDetails?.dateFormat ?? 'DD-MMM-YYYY';
  snackbarRef: any;

  user: any = [];
  groupList: any = [];
  primaryModuleList: any = [];
  moduleList: any = [];
  ownerList: any = [];
  dvCriticalityList: any = [];
  departmentList: any = [];
  typeList: any = [];
  requestedAtList: any = [];
  businessAreaList: any = [];
  reasonList: any = [];
  impactTypeList: any = [];
  referenceList: any = [];
  qualityUserList: any = [];
  evaluatorList: any = [];
  reporterTypeList: any = [];
  reporterTitleList: any = [];
  countryList: any = [];
  oostList: any = JSON.parse(sessionStorage.getItem('dropDownForOosType') || '{}');

  groupDropdown: any = [];
  moduleDropdown: any = [];
  ownerDropdown: any = [];
  dvCriticalityDropdown: any = [];
  departmentDropdown: any = [];
  typeDropdown: any = [];
  requestedAtDropdown: any = [];
  businessAreaDropdown: any = [];
  reasonDropdown: any = [];
  impactTypeDropdown: any = [];
  referenceDropdown: any = [];
  qualityUserDropdown: any = [];
  evaluatorDropdown: any = [];
  reporterTypeDropdown: any = [];
  reporterTitleDropdown: any = [];
  countryDropdown: any = [];
  oostDropdown: any = [];

  tableName: string = '';
  groupSearchText: string = '';
  moduleSearchText: string = '';
  ownerSearchText: string = '';
  departmentSearchText: string = '';
  initiatingDepartmentSearchText: string = '';
  typeSearchText: string = '';
  requestedAtSearchText: string = '';
  businessAreaSearchText: string = '';
  reasonSearchText: string = '';
  impactTypeSearchText: string = '';
  referenceSearchText: string = '';
  qualityUserSearchText: string = '';
  evaluatorSearchText: string = '';
  reporterTypeSearchText: string = '';
  reporterTitleSearchText: string = '';
  countrySearchText: string = '';
  oostSearchText: string = '';

  constructor(
    readonly appService: AppService,
    private readonly snackBar: MatSnackBar,
    private readonly elem: ElementRef,
    private readonly renderer: Renderer2,

    readonly dialog: MatDialog,
    readonly router: Router,
    readonly paginationService: PaginationService,
    readonly message: messageService,
    readonly qualityService: qmsService,
    readonly auService: AuService,
    readonly coreNavigationService: CoreNavigationService,
    readonly dependencyNavigationService: DependencyNavigationService,
  ) {
    const now = new Date();
    this.dateCustomClasses = [{ date: now, classes: ['bg-warning'] }];
  }

  private clickListener: () => void;
  private boundKeyDown = this.onKeyDown.bind(this);
  private boundKeyUp = this.onKeyUp.bind(this);
  private boundReset = this.resetKeyState.bind(this);

  ngOnInit() {
    document.addEventListener('keydown', this.boundKeyDown);
    document.addEventListener('keyup', this.boundKeyUp);
    window.addEventListener('blur', this.boundReset);
    document.addEventListener('contextmenu', this.boundReset);
    this.appService.getCodeListsByGroup("MODULES").subscribe((module: any) => {
      this.primaryModuleList = module;
    })
    this.getCall();
  }

  private main(): void {
    this.isProcessing = true;

    const getQualityGroups$ = this.appService.getQmsGroups();

    forkJoin([getQualityGroups$]).subscribe(([group]) => {

      this.departmentList = JSON.parse(sessionStorage.getItem('dropDownForDepartmentCode') ?? '{}');
      this.selectBoxForDepartment();

      this.groupList = group;
      this.selectBoxForGroup();

      this.isProcessing = false;
    });
  }


  ngAfterViewInit() {
    /* Listener for document click */
    this.clickListener = this.renderer.listen('document', 'click', () => {
      if (this.snackbarRef) {
        this.snackbarRef.dismiss();
      }
    });
  }

  ctrlPressed: boolean = false;
  shiftPressed: boolean = false;
  ngOnDestroy() {
    document.removeEventListener('keydown', this.boundKeyDown);
    document.removeEventListener('keyup', this.boundKeyUp);
    window.removeEventListener('blur', this.boundReset);
    document.removeEventListener('contextmenu', this.boundReset);
    if (this.clickListener) {
      this.clickListener();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.ctrlPressed = true;
    } else if (event.key === 'Shift') {
      this.shiftPressed = true;
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.ctrlPressed = false;
    } else if (event.key === 'Shift') {
      this.shiftPressed = false;
    }
  }

  resetKeyState() {
    this.ctrlPressed = false;
    this.shiftPressed = false;
  }

  onPaste(event: ClipboardEvent) {
    const isAllowedPaste = this.ctrlPressed && this.shiftPressed;
    if (!isAllowedPaste) {
      event.preventDefault();
      this.showSnackBar("You can't perform this operation. Please use Ctrl + Shift + V to paste the content", 'E');
    }
  }
  private selectBoxForGroup(): void {
    this.groupDropdown = this.groupList?.filter((x: any) => x.isActive == 'Y').map((group: any) => ({
      valueExp: group.groupId,
      displayExp: group.groupName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onGroupSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.dependency.module = null;
      if (item) {
        this.isProcessing = true;
        this.appService.getQmsGroupByGroupId(item.valueExp).subscribe((group: any) => {
          const splittedValues: any[] = group.modules?.split(',') ?? [];
          if (splittedValues.length) {
            this.moduleList = this.primaryModuleList.filter((module: any) => splittedValues.includes(module.codelistCode));
            this.selectBoxForModule();
          } else {
            setTimeout(() => this.showSnackBar("Sorry there are no modules configured for the group !", 'E'));
          }
          this.isProcessing = false;
        })
      } else {
        this.moduleList = this.moduleDropdown = [];
      }
    }
  }

  public onMapGroupSelect(event: any, item: any): void {
    if (event.isUserInput) {
      if (item) {

        const x = this.mapDependency.module;

        if (x == 'CR' || x == 'CAPA' || x == 'DV' || x == 'OOS') this.mapDependency.role = 'Owner';
        if (x == 'IN' || x == 'QI' || x == 'CC') this.mapDependency.role = 'Evaluator';
        if (x == 'AU') this.mapDependency.role = 'Planner';

        this.isProcessing = true;
        this.appService.getQmsActiveGroupUsers(item.valueExp, this.mapDependency.module, this.mapDependency.role).subscribe((data: any) => {
          this.ownerList = data;
          this.selectBoxForOwner();
          this.isProcessing = false;
        })
      } else {
        this.ownerList = [];
      }
    }
  }

  private selectBoxForModule(): void {
    this.moduleDropdown = this.moduleList?.filter((x: any) => x.codelistCode != 'AU')?.map((group: any) => ({
      valueExp: group.codelistCode,
      displayExp: group.codelistName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onModuleSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.reset(item.valueExp);
      this.dependency = { ...this.dependency, ...SECONDARY_RESET_DEPENDENCY };

      if (item) {
        this.isProcessing = true;
        this.appService.getQmsActiveUserByGroupIdModuleUserId(this.dependency.groupId, item.valueExp).subscribe((user: any) => {
          this.user = user;
          if (this.user.length) {
            if (this.user[0].qmsRoles.includes("Initiator")) {
              this.dependency.showModule = true;
              this.getDynamicLabel(item.valueExp);
              this.getModuleSpecificDetails();
            } else {
              this.dependency.showModule = false;
              this.dependency.module = null;
              this.isProcessing = false;
              this.showSnackBar("Sorry you does not have Initiator Privilege !", 'E');
            }
          } else {
            this.dependency.showModule = false;
            this.dependency.module = null;
            this.isProcessing = false;
            this.showSnackBar("Sorry you does not have Initiator Privilege !", 'E');
          }
        });
      } else {
        this.dependency = { ...SECONDARY_RESET_DEPENDENCY };
      }
    }
  }

  private reset(module: any): void {
    if (module == 'CR') this.cr = { ...RESET_CR };
    if (module == 'CAPA') this.capa = { ...RESET_CAPA };
    if (module == 'DV') this.dv = { ...RESET_DV };
    if (module == 'IN') this.incident = { ...RESET_IN };
    if (module == 'QI') this.qi = { ...RESET_QI };
    if (module == 'CC') this.cc = { ...RESET_CC };
    if (module == 'OOS') {
      this.oos = { ...RESET_OOS };

      if (this.dependency.groupDetail?.activityClosureDays) {
        const today = new Date();
        today.setDate(today.getDate() + this.dependency.groupDetail?.activityClosureDays);
        this.oos.expcetedClosureOn = today;
      }
    }
  }

  private getModuleSpecificDetails(): void {
    this.appService.getQmsGroupDetailsByModule(this.dependency.groupId, this.dependency.module).subscribe((groupDetail: any) => {
      this.dependency.groupDetail = groupDetail;
      this.getChildData(this.dependency.groupId, this.dependency.module);
    });
  }

  private getChildData(groupId: any, module: any): void {

    this.isProcessing = true;

    const $requests: Observable<any>[] = [];
    const reasonMapping: Record<string, string> = { 'CR': 'REASON_FOR_CHANGE', 'CAPA': 'REASON_FOR_CAPA', 'DV': 'REASON_FOR_DEVIATION', };
    const impactMapping: Record<string, string> = { 'IN': 'INCIDENT_IMPACT_TYPE', 'QI': 'QI_IMPACT_TYPE' };
    const referenceMapping: Record<string, string> = { 'IN': 'INCIDENT_REFERENCE', 'QI': 'QUALITY_ISSUE_REFERENCE' };

    if (['CC'].includes(module)) {
      $requests.push(of(null));
    } else {
      $requests.push(this.appService.getTypeSettingsByGrpIdGrpDetailId(groupId, this.dependency.groupDetail.groupDetailId));
    }

    if (['AU', 'OOS', 'CC'].includes(module)) {
      $requests.push(of(null));
    } else {
      $requests.push(this.appService.getQmsBusinessAreasByModule(module));
    }

    if (['CR', 'CAPA', 'DV', 'OOS'].includes(module)) {
      $requests.push(
        this.appService.getQmsActiveGroupUsers(groupId, module, 'Owner'),
      );
    } else {
      $requests.push(of(null), of(null));
    }

    if (['CR', 'CAPA', 'DV'].includes(module)) {
      $requests.push(
        this.appService.getCodeListsByGroup(reasonMapping[module])
      );
    } else {
      $requests.push(of(null));
    }

    if (['DV'].includes(module)) {
      $requests.push(
        this.appService.getCodeListsByGroup("DEVIATION_CRITICALITY"),
      );
    } else {
      $requests.push(of(null));
    }

    if (['IN', 'QI'].includes(module)) {
      $requests.push(
        this.appService.getQmsActiveGroupUsers(groupId, module, 'Evaluator'),
        this.appService.getCodeListsByGroup(impactMapping[module]),
        this.appService.getCodeListsByGroup(referenceMapping[module])
      );
    } else {
      $requests.push(of(null), of(null), of(null));
    }

    if (['IN'].includes(module)) {
      $requests.push(this.appService.getModuleBasedPrivilegedUsersList('QMS'));
    } else {
      $requests.push(of(null));
    }

    if (['CC'].includes(module)) {
      $requests.push(
        this.appService.getCodeListsByGroup("COMPLAINT_REPORTER_TITLE"),
        this.appService.getCodeListsByGroup("COMPLAINT_REPORTER_TYPE"),
        this.appService.getNonOrgCodeListsByGroupName("COUNTRY"));
    } else {
      $requests.push(of(null), of(null), of(null));
    }

    forkJoin($requests).subscribe(([type, businessArea, owner, reason, criticality, evaluator, impactType, reference, qualityUsers,
      reporterTitle, reporterType, country
    ]) => {

      this.dvCriticalityList = criticality ?? [];
      this.dvCriticalityList && this.selectBoxForDeviationCriticality();

      this.ownerList = owner ?? [];
      this.ownerList && this.selectBoxForOwner();

      this.requestedAtList = businessArea ?? [];
      this.requestedAtList && this.selectBoxForRequestedAt();

      this.typeList = type ?? [];
      this.typeList.length && this.selectBoxForType();

      this.reasonList = reason ?? [];
      this.reasonList.length && this.selectBoxForReason();

      this.impactTypeList = impactType ?? [];
      this.impactTypeList.length && this.selectBoxForImpactType();

      this.referenceList = reference ?? [];
      this.referenceList.length && this.selectBoxForReference();

      this.qualityUserList = qualityUsers ?? [];
      this.qualityUserList.length && this.selectBoxForQualityUsers(false);

      this.evaluatorList = evaluator ?? [];
      this.evaluatorList.length && this.selectBoxForEvaluator();

      this.reporterTitleList = reporterTitle ?? [];
      this.reporterTitleList.length && this.selectBoxForReporterTitle();

      this.reporterTypeList = reporterType ?? [];
      this.reporterTypeList.length && this.selectBoxForReporterType();

      this.countryList = country ?? [];
      this.countryList.length && this.selectBoxForReporterCountry();

      this.selectBoxForOosType();
      this.isProcessing = false;
    });
  }

  private selectBoxForDepartment(): void {
    this.departmentDropdown = this.departmentList?.map((group: any) => ({
      valueExp: group.codelistCode,
      displayExp: group.codelistName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onDepartmentSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-dept', item);
    }
  }

  public onInitiatingDepartmentSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation('initiating-dept', item);
    }
  }

  private selectBoxForOwner(): void {
    this.ownerDropdown = this.ownerList?.map((owner: any) => ({
      valueExp: owner.userId,
      displayExp: owner.userName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onOwnerSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-owner', item);
    }
  }

  private selectBoxForType(): void {
    this.typeDropdown = this.typeList?.map((item: any) => ({
      valueExp: item.type,
      displayExp: item.type
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) || []
  }

  public onTypeSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation(this.dependency.module + '-type', item);

      if (item) {
        this.appService.getTypeSettingsByType(this.dependency.groupId, this.dependency.groupDetail.groupDetailId, item.valueExp).subscribe((response: any) => {
          if (response) {
            if (response.toBeStartedInDays) {
              let date = new Date();
              date.setDate(date.getDate() + response.toBeStartedInDays);
              this.setExpectedToBeStartedOn(this.dependency.module!, date);
            } else {
              this.setExpectedToBeStartedOn(this.dependency.module!, null);
            }

            if (response.toBeClosedInDays) {
              let date = new Date();
              this.setExpectedClosureOn(this.dependency.module!, response, date);
            } else {
              this.setExpectedClosureOn(this.dependency.module!, null, null);
            }
          }
          this.isProcessing = false;
        })
      }
    }
  }

  private selectBoxForDeviationCriticality(): void {
    this.dvCriticalityDropdown = this.dvCriticalityList?.map((criticality: any) => ({
      valueExp: criticality.codelistCode,
      displayExp: criticality.codelistName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onDvCriticalitySelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation('dv-criticality', item);
    }
  }

  private setExpectedToBeStartedOn(module: string, date: Date | null): void {
    const map: any = { 'CR': this.cr, 'CAPA': this.capa, 'DV': this.dv, 'IN': this.incident, 'QI': this.qi };
    const target = map[module];

    target.expectedToBeStartedOn = date;
    date && this.onNgModelChange(target.expectedToBeStartedOn, this.dependency.module?.toLowerCase() + '-ex-to-be-started-on');
  }

  private setExpectedClosureOn(module: string, response: any, date: Date | null): void {
    const map: any = { 'CR': this.cr, 'CAPA': this.capa, 'DV': this.dv, 'IN': this.incident, 'QI': this.qi };
    const target = map[module];

    if (!response) return;

    if (!date) { target.expcetedClosureOn = null; return; }

    const days = target.expectedToBeStartedOn == null ? response.toBeClosedInDays : response.toBeStartedInDays + response.toBeClosedInDays;
    date.setDate(date.getDate() + days);
    target.expcetedClosureOn = date;
    date && this.onNgModelChange(target.expcetedClosureOn, this.dependency.module?.toLowerCase() + '-closure-on');
  }

  private selectBoxForRequestedAt(): void {
    this.requestedAtDropdown = this.requestedAtList?.filter((x: any) => x.module == this.dependency.module)?.map((item: any) => ({
      valueExp: item.taskRequestedAt,
      displayExp: item.taskRequestedAt
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) || []
  }

  @ViewChild('flexible') flexibleComponent!: FlexibleFieldsComponent;

  public onRequestedAtSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-requested-at', item);

      if (item) {
        const map: any = { 'CR': this.cr, 'CAPA': this.capa, 'DV': this.dv, 'IN': this.incident, 'QI': this.qi };
        const target = map[this.dependency.module!];

        this.dependency.requestedAtName = item.displayExp;
        const getBusinessArea = this.requestedAtList?.find((x: any) => x.taskRequestedAt == item.displayExp);
        this.dependency.isBusinessAreaConfigured = !(getBusinessArea?.listOfBusinessArea?.trim());
        [this.businessAreaList, this.dependency.selectedBusinessArea, target.coreBusinessAreaName] = [[], [], ''];
        this.businessAreaList = getBusinessArea?.listOfBusinessArea?.split(',');
        this.selectBoxForBusinessArea();

        setTimeout(() => { this.flexibleComponent.triggerFromRequestedAt(item.valueExp) })
      } else {
        this.businessAreaList = [];
        this.businessAreaDropdown = [];
        this.dependency.requestedAtName = '';
      }
    }
  }

  private selectBoxForBusinessArea(): void {
    this.businessAreaDropdown = this.businessAreaList?.map((bArea: any) => ({
      valueExp: bArea,
      displayExp: bArea
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) || []
  }

  public onBusinessAreaSelect(event: any, item: any) {
    if (event.isUserInput)
      setTimeout(() => {
        this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-business-area', this.dependency.selectedBusinessArea)
        this.dependency.isAllBusinessAreaSelected = this.businessAreaDropdown.length == this.dependency.selectedBusinessArea.length;
      });
  }

  private selectBoxForReason(): void {
    this.reasonDropdown = this.reasonList?.map((group: any) => ({
      valueExp: group.codelistCode,
      displayExp: group.codelistName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  visibleReasonForOthers: boolean = false;
  public onReasonSelect(event: any, item: any): void {
    if (event.isUserInput) {
      if (item) {
        if (item.displayExp.toLowerCase() == 'others') { this.visibleReasonForOthers = true; this.clearReason(this.dependency.module as string); }
        else this.visibleReasonForOthers = false;
        setTimeout(() => {
          this.dependency.isAllReasonSelected = this.reasonDropdown.length == this.dependency.selectedReason.length;
          this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-reason', this.dependency.selectedReason);
        });
      }
    }
  }

  private clearReason(module: string): void {
    if (module == 'CR') {
      this.cr.reasonForChange = null;
    } else if (module == 'CAPA') {
      this.capa.reasonForCapa = null;
    } else if (module == 'DV') {
      this.dv.reasonForDeviation = null;
    }
  }

  private selectBoxForReference(): void {
    this.referenceDropdown = this.referenceList.map((item: any) => ({
      valueExp: item.codelistCode,
      displayExp: item.codelistName
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m.displayExp.trim().localeCompare(n.displayExp.trim())) || []
  }

  visibleReferenceIfOthers: boolean = false;
  public onReferenceSelect(event: any, item: any) {
    if (event.isUserInput) {
      this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-reference', item);
      if (item) {
        if (item.displayExp.toLowerCase() == 'Others') { this.visibleReferenceIfOthers = true; this.clearReference(this.dependency.module as string); }
        else this.visibleReferenceIfOthers = false;
      } else {
        this.visibleReferenceIfOthers = false;
      }
    }
  }

  private clearReference(module: string): void {
    if (module == 'IN') {
      this.incident.referenceOthers = null;
    } else if (module == 'QI') {
      this.qi.reference = null;
    }
  }

  private selectBoxForImpactType(): void {
    this.impactTypeDropdown = this.impactTypeList.map((item: any) => ({
      valueExp: item.codelistCode,
      displayExp: item.codelistName
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m.displayExp.trim().localeCompare(n.displayExp.trim())) || []
  }

  public onImpactTypeSelect(event: any, item: any): void {
    if (event.isUserInput)
      this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-impact-type', item);
  }

  private selectBoxForQualityUsers(isVendor: boolean): void {
    this.qualityUserDropdown = this.qualityUserList.map((item: any) => ({
      valueExp: isVendor ? item.vendorId : item.userId,
      displayExp: isVendor ? item.vendorName : item.userName
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m.displayExp.trim().localeCompare(n.displayExp.trim()));
  }

  public onIsOutsideOrganizationChange(event: any): void {
    this.isProcessing = true;
    this.incident.initiatedOnBehalf = null;

    if (event.checked) {
      this.appService.getQmsVendorsVwDetails().subscribe((data: any) => {
        this.qualityUserList = data;
        this.isProcessing = false;
        this.selectBoxForQualityUsers(true);
      })
    } else {
      this.appService.getModuleBasedPrivilegedUsersList('QMS').subscribe((data: any) => {
        this.qualityUserList = data;
        this.isProcessing = false;
        this.selectBoxForQualityUsers(false);
      })
    }
  }

  public onBehalfSelect(event: any, item: any) {
    if (event.isUserInput)
      this.handleDropDownValidation('behalf-on-others', item);
  }

  private selectBoxForEvaluator(): void {
    this.evaluatorDropdown = this.evaluatorList?.map((owner: any) => ({
      valueExp: owner.userId,
      displayExp: owner.userName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onEvaluatorSelect(event: any, item: any): void {
    if (event.isUserInput) {
      this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-evaluator', item);
    }
  }

  private selectBoxForReporterTitle(): void {
    this.reporterTitleDropdown = this.reporterTitleList.map((item: any) => ({
      valueExp: item.codelistCode,
      displayExp: item.codelistName
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m.displayExp.trim().localeCompare(n.displayExp.trim())) || []
  }

  public onReporterTitleSelect(event: any, item: any): void {
    if (event.isUserInput)
      this.handleDropDownValidation('title', item);
  }

  private selectBoxForReporterType(): void {
    this.reporterTypeDropdown = this.reporterTypeList.map((item: any) => ({
      valueExp: item.codelistCode,
      displayExp: item.codelistName
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m.displayExp.trim().localeCompare(n.displayExp.trim())) || []
  }

  public onReporterTypeSelect(event: any, item: any) {
    if (event.isUserInput)
      this.handleDropDownValidation('reporter-type', item);
  }

  private selectBoxForReporterCountry(): void {
    this.countryDropdown = this.countryList.map((item: any) => ({
      valueExp: item.codelistCode,
      displayExp: item.codelistName
    })).sort((m: { displayExp: string }, n: { displayExp: string }) => m.displayExp.trim().localeCompare(n.displayExp.trim())) || []
  }

  private selectBoxForOosType(): void {
    this.oostDropdown = this.oostList.map((oosType: any) => ({
      valueExp: oosType.codelistCode,
      displayExp: oosType.codelistName
    }))
  }

  public onOostTypeSelect(event: any, item: any): void {
    if (event.isUserInput)
      this.handleDropDownValidation('oost-type', item);
  }


  public getTooltip(): string {
    return this.dependency.selectedBusinessArea.join(',');
  }

  @ViewChild('businessArea') businessArea: MatSelect;
  public toggleAllBusinessArea(): void {
    if (this.dependency.isAllBusinessAreaSelected) {
      for (const item of this.businessArea.options) item.select();
    } else {
      for (const item of this.businessArea.options) item.deselect();
      this.dependency.selectedBusinessArea = [];
    }

    setTimeout(() => { this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-business-area', this.dependency.selectedBusinessArea); });
  }

  @ViewChild('reason') reason: MatSelect;
  public toggleAllReason(): void {
    if (this.dependency.isAllReasonSelected) {
      for (const item of this.reason.options) item.select();
    } else {
      for (const item of this.reason.options) item.deselect();
      this.dependency.selectedReason = [];
    }
    this.visibleReasonForOthers = this.dependency.isAllReasonSelected!;
    setTimeout(() => { this.handleDropDownValidation(this.dependency.module?.toLowerCase() + '-reason', this.dependency.selectedReason); });
  }

  public focusSearch(value: any): void {
    setTimeout(() => { let requiredElement = document.getElementById(value); requiredElement?.focus(); }, 0)
  };

  public handleDropDownValidation(fieldId: string, selectedItems: any[] | string): void {
    const field = document.getElementById(fieldId) as HTMLInputElement;
    const isMultiSelectValidation = fieldId == 'cr-business-area' || fieldId == 'cr-reason' || fieldId == 'capa-business-area' || fieldId == 'capa-reason' || fieldId == 'dv-business-area' || fieldId == 'dv-reason' || fieldId == 'in-business-area' || fieldId == 'qi-business-area';
    const shouldBeRed = isMultiSelectValidation ? !selectedItems.length : !selectedItems;

    field && (field.style.color = shouldBeRed ? 'red' : '#005c8a');
    !field && console.log('Unable to find the element. Id → ', fieldId);
  }

  public onNgModelChange(items: any, fieldId: string): void {
    const field = document.getElementById(fieldId) as HTMLInputElement;

    field && (field.style.color = items ? '#005c8a' : 'red');
    !field && console.log('Unable to find the element. Id → ', fieldId);
  }

  public onStEditorChange(items: any, fieldId: string): void {
    const field = document.getElementById(fieldId) as HTMLInputElement;
    let isEmpty: boolean = false;
    items && (isEmpty = this.isContentEmpty(items));
    field && (field.style.color = isEmpty ? 'red' : '#005c8a');
    !field && console.log('Unable to find the element. Id → ', fieldId);
  }

  private isContentEmpty(content: string): boolean {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const textContent = (tempDiv.textContent ?? tempDiv.innerText) || '';
    return textContent.trim().length === 0;
  }

  public limitCharacters(event: Event, field: any) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const textLength = target.value.length;
    let charLimit: any = null;

    const fieldMapping: Record<number, string[]> = {
      1000: ['REASON_FOR_CHANGE', 'BUSINESS_AREA']
    };

    for (const [limit, fields] of Object.entries(fieldMapping)) {
      if (fields.includes(field)) {
        charLimit = limit;
        break;
      }
    }

    if (textLength > charLimit) {
      this.showSnackBar("Maximum " + charLimit + " Characters!", 'E');
      target.value = target.value.substring(0, charLimit);
    }
  }

  public validateMobileNumber(event: KeyboardEvent): void {
    const forbiddenKeys = ['E', 'e', '-', '*', '.'];
    if (forbiddenKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  public validateZipCode(event: KeyboardEvent): void {
    const forbiddenKeys = ['E', 'e', '-', '*', '.', '+'];
    if (forbiddenKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  public validateAddress(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    const textLength = target.value.length;

    if (textLength > 400) {
      this.showSnackBar("Maximum 400 Characters!", 'E');
      target.value = target.value.substring(0, 400);
    }
  }

  public readonly $commonDateConfig: Readonly<Partial<BsDatepickerConfig>> = {
    dateInputFormat: this.dateFormat,
    showWeekNumbers: false,
    containerClass: 'theme-dark-blue',
    showTodayButton: false,
    todayPosition: 'center'
  }

  public readonly $present_future_config: Readonly<Partial<BsDatepickerConfig>> = {
    ...this.$commonDateConfig,
    minDate: new Date()
  }

  public readonly $past_present_config: Readonly<Partial<BsDatepickerConfig>> = {
    ...this.$commonDateConfig,
    maxDate: new Date()
  }

  public readonly dateTimeConfig: Readonly<Partial<BsDatepickerConfig>> = {
    ...this.$commonDateConfig,
    dateInputFormat: this.dateFormat + ' HH:mm:ss'
  }

  public $getExpectedClosureDateConfig(): Readonly<Partial<BsDatepickerConfig>> {
    return {
      ...this.$commonDateConfig,
      ...(this.dependency.module == 'CR') && { minDate: this.cr.expectedToBeStartedOn ? new Date(this.cr.expectedToBeStartedOn) : new Date() },
      ...(this.dependency.module == 'CAPA') && { minDate: this.capa.expectedToBeStartedOn ? new Date(this.capa.expectedToBeStartedOn) : new Date() },
      ...(this.dependency.module == 'DV') && { minDate: this.dv.expectedToBeStartedOn ? new Date(this.dv.expectedToBeStartedOn) : new Date() },
      ...(this.dependency.module == 'IN') && { minDate: this.incident.expectedToBeStartedOn ? new Date(this.incident.expectedToBeStartedOn) : new Date() },
      ...(this.dependency.module == 'QI') && { minDate: this.qi.expectedToBeStartedOn ? new Date(this.qi.expectedToBeStartedOn) : new Date() },
      ...(this.dependency.module == 'OOS') && { minDate: this.oos.expectedToBeStartedOn ? new Date(this.oos.expectedToBeStartedOn) : new Date() },
    }
  }

  public $getIdentifiedOnConfig(): Readonly<Partial<BsDatepickerConfig>> {
    let x: any;

    if (this.dependency.module == 'DV') x = this.dv.deviationOccuredOn;
    if (this.dependency.module == 'OOS') x = this.oos.dateOfOccurrence;
    return {
      ...this.$commonDateConfig,
      minDate: x ? new Date(x) : new Date(),
      maxDate: new Date()
    }
  }

  userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
  onExpectedToBeStartedOnChange(items: any) {
    const localDate = moment.tz(items, this.userTimeZone).format();
    console.log('Selected Date in User Time Zone:', localDate);
    const utcDate = moment(items).utc().format();
    console.log('Selected Date in UTC:', utcDate);

    if (this.dependency.module == 'CR' && this.cr.expcetedClosureOn && (this.cr.expcetedClosureOn < this.cr.expectedToBeStartedOn!)) this.cr.expcetedClosureOn = null;
    if (this.dependency.module == 'CAPA' && this.capa.expcetedClosureOn && (this.capa.expcetedClosureOn < this.capa.expectedToBeStartedOn!)) this.capa.expcetedClosureOn = null;
    if (this.dependency.module == 'DV' && this.dv.expcetedClosureOn && (this.dv.expcetedClosureOn < this.dv.expectedToBeStartedOn!)) this.dv.expcetedClosureOn = null;
    if (this.dependency.module == 'IN' && this.incident.expcetedClosureOn && (this.incident.expcetedClosureOn < this.incident.expectedToBeStartedOn!)) this.incident.expcetedClosureOn = null;
    if (this.dependency.module == 'QI' && this.qi.expcetedClosureOn && (this.qi.expcetedClosureOn < this.qi.expectedToBeStartedOn!)) this.qi.expcetedClosureOn = null;

  }

  onOosOccurrenceChange(items: any) {
    if (this.oos.identifiedOn && (this.oos.dateOfOccurrence! > this.oos.identifiedOn)) this.oos.identifiedOn = null;
  }

  @ViewChild('editor', { static: true }) editor: TemplateRef<any>;
  visibleEditor: MatDialogRef<TemplateRef<any>>;

  editorConfig = {
    width: '100%',
    height: 'auto',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '30px' },
  };

  excludeButtons: EditorConfig = {
    buttons: [],
  };

  includeButtons: EditorConfig = {
    buttons: ST_BUTTONS,
  };

  editorName: string = '';
  editorTitle: string = '';
  editorValue: string = '';
  public openEditor(value: string): void {

    const editorTitleMapping: Record<string, string> = {
      'Change Description': this.dynamicLabel?.['CHANGE_DESCRIPTION']?.fieldName,
      'Change Justification': this.dynamicLabel?.['CHANGE_JUSTIFICATION']?.fieldName,
      'CAPA Description': this.dynamicLabel?.['CAPA_DESCRIPTION']?.fieldName,
      'Deviation Description': this.dynamicLabel?.['DV_DESCRIPTION']?.fieldName,
      'Impact Description': this.dynamicLabel?.['IMPACT_DESCRIPTION']?.fieldName,
      'Incident Description': this.dynamicLabel?.['DESCRIPTION']?.fieldName,
      'Description': this.dynamicLabel?.['DESCRIPTION']?.fieldName,
      'Reporter Comments': this.dynamicLabel?.['REPORTER_COMMENTS']?.fieldName
    };

    this.editorName = value;
    this.editorTitle = editorTitleMapping[this.editorName];
    if (value == 'Change Description') {
      this.editorValue = this.cr.changeDescription as string;
    } else if (value == 'Change Justification') {
      this.editorValue = this.cr.changeJustification as string;
    } else if (value == 'CAPA Description') {
      this.editorValue = this.capa.capaDescription as string;
    } else if (value == 'Deviation Description') {
      this.editorValue = this.dv.dvDescription as string;
    } else if (value == 'Impact Description' && this.dependency.module == 'IN') {
      this.editorValue = this.incident.impactDescription as string;
    } else if (value == 'Impact Description' && this.dependency.module == 'QI') {
      this.editorValue = this.qi.impactDescription as string;
    } else if (value == 'Incident Description') {
      this.editorValue = this.incident.description as string;
    } else if (value == 'Description') {
      this.editorValue = this.qi.description as string;
    } else if (value == 'Reporter Comments') {
      this.editorValue = this.cc.reporterComments as string;
    }

    this.visibleEditor = this.dialog.open(this.editor, this.editorConfig);
  }

  public saveEditor(): void {
    if (this.editorName == 'Change Description') {
      this.cr.changeDescription = this.editorValue;
    } else if (this.editorName == 'Change Justification') {
      this.cr.changeJustification = this.editorValue;
    } else if (this.editorName == 'CAPA Description') {
      this.capa.capaDescription = this.editorValue;
    } else if (this.editorName == 'Deviation Description') {
      this.dv.dvDescription = this.editorValue;
    } else if (this.editorName == 'Impact Description' && this.dependency.module == 'IN') {
      this.incident.impactDescription = this.editorValue;
    } else if (this.editorName == 'Impact Description' && this.dependency.module == 'QI') {
      this.qi.impactDescription = this.editorValue;
    } else if (this.editorName == 'Incident Description') {
      this.incident.description = this.editorValue;
    } else if (this.editorName == 'Description') {
      this.qi.description = this.editorValue;
    } else if (this.editorName == 'Reporter Comments') {
      this.cc.reporterComments = this.editorValue;
    }

    this.visibleEditor.close();
  }

  public cancelEditor(): void {
    this.visibleEditor.close();
  };

  @ViewChild('addDependencyPopup', { static: true }) addDependencyPopup: TemplateRef<any>;
  private visibleAddDependencyPopup: MatDialogRef<TemplateRef<any>>;
  @ViewChild('deleteDependencyPopup', { static: true }) deleteDependencyPopup: TemplateRef<any>;
  private visibleDeleteDependencyPopup: MatDialogRef<TemplateRef<any>>;

  private readonly addDependencyPopupConfig: MatDialogConfig = {
    width: '100%',
    height: 'auto',
    panelClass: 'my-dialog',
    hasBackdrop: true,
    disableClose: true,
    position: { top: '10px' },
  };

  private readonly deleteDependencyPopupConfig: MatDialogConfig = {
    width: '500px',
    height: 'auto',
    panelClass: 'my-dialog',
    hasBackdrop: false,
    disableClose: true,
    position: { top: '10px' },
  };

  public openAddDependencyPopup(mode: any): void {
    this.main();
    this.dependency = { ...RESET_DEPENDENCY };
    this.mapDependency = { ...RESET_MAP_DEPENDENCY };
    this.dependency.groupId = null;
    this.dependency.module = null;
    this.dependency.mode = mode;
    this.popup.title = mode == 'create' ? 'Add Dependency' : 'Map Dependency';
    mode == 'map' && this.selectBoxForMapModule();
    this.visibleAddDependencyPopup = this.dialog.open(this.addDependencyPopup, this.addDependencyPopupConfig);
    console.log(mode);
  }

  private selectBoxForMapModule(): void {
    const x: any[] = [...this.primaryModuleList];
    this.moduleDropdown = x?.filter((y: any) => !['DOCS', 'TRN', 'BP', 'AU'].includes(y.codelistCode)).map((group: any) => ({
      valueExp: group.codelistCode,
      displayExp: group.codelistName
    }))?.sort((m: { displayExp: any }, n: { displayExp: any }) => m?.displayExp?.trim()?.localeCompare(n?.displayExp?.trim())) ?? []
  }

  public onMapModuleSelect(event: any, item: any) {
    if (event.isUserInput) {
      if (item) {

        this.isProcessing = true;
        this.mapDependency.selectedModule = item.displayExp;

        this.appService.getQmsGroupsByModule(item.valueExp).subscribe((groups: any) => {
          this.groupList = groups;
          this.isProcessing = false;
          this.selectBoxForGroup();
        })
      }
    }
  }

  fieldValidation(elementIds: string[]) {
    for (const elementId of elementIds) {
      const element = document.getElementById(elementId) as HTMLInputElement | null;
      if (element) {
        element.style.color = 'red';
      }
    }
  }

  public searchDependencies(mapDependency: MapDependency): void {
    if (this.mapDependency.module) {
      this.isProcessing = true;

      const obj: {} = {
        module: mapDependency.module,
        taskId: mapDependency.refNo ? mapDependency.refNo : null,
        deptCode: mapDependency.department,
        status: mapDependency.status,
        owner: mapDependency.owner,
        groupId: this.mapDependency.groupId ?? null
      }
      this.appService.searchAllTasksVwDetails(obj).subscribe((tasks: any) => {
        this.isProcessing = false;
        if (tasks.length) {
          if (mapDependency.module == 'AU') {
            this.mapDependency.dependencies = tasks.filter((item: any) => !auditStatus.includes(item.status))
          } else {
            this.mapDependency.dependencies = tasks;
          }
        } else {
          this.mapDependency.dependencies = [];
        }
      })
    } else {
      this.onNgModelChange(this.mapDependency.module, 'map-module');
      this.showSnackBar('Please select the module', 'E');
    }
  }

  public mapReset(): void {
    this.mapDependency = { ...RESET_MAP_DEPENDENCY };
  }

  public onChecked(e: any, d: any): void {
    this.mapDependency.dependencies.every((item: any) => { if (item.childCheckBox) { item.childCheckBox = false; } return true; })

    if (e.target.checked) {
      d.childCheckBox = true;
      this.mapDependency.mappingDependency = d;
    } else {
      d.childCheckBox = false;
      this.mapDependency.mappingDependency = {};
    }
  }

  public create(module: any): void {
    if (this.dependency.mode == 'create') {
      if (!this.dependency.module) { setTimeout(() => this.showSnackBar('Please select the Module', 'E')); return; }
      if (!this.dependency.groupId) { setTimeout(() => this.showSnackBar('Please select the Group', 'E')); return; }
      this.trigger(module);

    } else if (this.dependency.mode == 'map') {
      if (!this.mapDependency.mappingDependency) { setTimeout(() => this.showSnackBar('Please select the dependency to map', 'E')); return; }
      if (this.mapDependency.mappingDependency.taskNo == this.qualityService.activityId) { setTimeout(() => this.showSnackBar('Sorry cannot map the same activity !', 'E')); return; }
      this.map();

    }
  }

  private map(): void {

    for (let dependency of this.grid) {
      if (this.mapDependency.mappingDependency.taskNo == dependency.DEPENDENT_REF_NO) {
        setTimeout(() => { this.showSnackBar("This activity has been already added as a Dependency", 'E') });
        return;
      }
    }

    this.isProcessing = true;
    const obj: {} = {
      module: this.qualityService.moduleName,
      refNo: this.qualityService.activityId,
      dependentModule: this.mapDependency.mappingDependency.module,
      dependentRefNo: this.mapDependency.mappingDependency.taskNo,
    }
    this.appService.createQmsTaskDependencies(obj).subscribe({
      next: (Response: any) => {
        if (Response.resultType == "S") {
          this.visibleAddDependencyPopup.close();
          this.mapDependency = { ...RESET_MAP_DEPENDENCY };
          this.getCall();
          this.showSnackBar(Response.result, Response.resultType);
        } else {
          this.isProcessing = false;
          this.showSnackBar(Response.result, Response.resultType);
        }
      },
      error: (error: any) => {
        this.isProcessing = false;
        throw error;
      }
    });
  }

  private trigger(module: any): void {
    if (module == 'CR') {
      this.validateCr(this.cr);
    } else if (module == 'CAPA') {
      this.validateCapa(this.capa);
    } else if (module == 'DV') {
      this.validateDv(this.dv);
    } else if (module == 'IN') {
      this.validateIn(this.incident);
    } else if (module == 'QI') {
      this.validateQi(this.qi);
    } else if (module == 'CC') {
      this.validateCc(this.cc);
    } else if (module == 'OOS') {
      this.validateOos(this.oos);
    }
  }

  private validateCr(cr: CR): void {
    const validations = [
      { condition: !cr.deptCode && this.dynamicLabel['DEPT_NAME'].isRemovable != 'Y', id: 'cr-dept' },
      { condition: !cr.owner, id: 'cr-owner' },
      { condition: !cr.requestedAt, id: 'cr-requested-at' },
      { condition: this.businessAreaDropdown.length ? !this.dependency.selectedBusinessArea.length : !cr.coreBusinessAreaName, id: 'cr-business-area' },
      { condition: this.dynamicLabel['EXPECTED_TO_BE_STARTED_ON'].isRemovable != 'Y' && !cr.expectedToBeStartedOn, id: 'cr-ex-to-be-started-on' },
      { condition: !cr.expcetedClosureOn, id: 'cr-closure-on' },
      { condition: !cr.shortDescription, id: 'cr-short-description' },
      { condition: !cr.changeDescription && this.dynamicLabel['CHANGE_DESCRIPTION']?.isRemovable != 'Y', id: 'cr-description' },
      { condition: !cr.changeJustification && this.dynamicLabel['CHANGE_JUSTIFICATION']?.isRemovable != 'Y', id: 'cr-justification' },
      { condition: this.dynamicLabel['REASON_FOR_CHANGE_CODE'].isRemovable != 'Y' && !this.dependency.selectedReason.length, id: 'cr-reason' },
      { condition: this.visibleReasonForOthers && !cr.reasonForChange, id: 'cr-reason-if-others' },
    ]

    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setCrJson(cr);
    }
  }

  private validateCapa(capa: CAPA): void {
    const validations = [
      { condition: !capa.deptCode && this.dynamicLabel['DEPT_NAME'].isRemovable != 'Y', id: 'capa-dept' },
      { condition: !capa.owner, id: 'capa-owner' },
      { condition: !capa.requestedAt, id: 'capa-requested-at' },
      { condition: this.businessAreaDropdown.length ? !this.dependency.selectedBusinessArea.length : !capa.coreBusinessAreaName, id: 'capa-business-area' },
      { condition: this.dynamicLabel['EXPECTED_TO_BE_STARTED_ON'].isRemovable != 'Y' && !capa.expectedToBeStartedOn, id: 'capa-ex-to-be-started-on' },
      { condition: !capa.expcetedClosureOn, id: 'capa-closure-on' },
      { condition: !capa.shortDescription, id: 'capa-short-description' },
      { condition: !capa.capaDescription, id: 'capa-description' },
      { condition: this.dynamicLabel['REASON_FOR_CAPA_CODE'].isRemovable != 'Y' && !this.dependency.selectedReason.length, id: 'capa-reason' },
      { condition: this.visibleReasonForOthers && !capa.reasonForCapa, id: 'capa-reason-if-others' },
    ]

    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setCapaJson(capa);
    }
  }

  private validateDv(dv: DV): void {
    const validations = [
      { condition: !dv.deptCode && this.dynamicLabel['DEPT_NAME'].isRemovable != 'Y', id: 'dv-dept' },
      { condition: !dv.owner, id: 'dv-owner' },
      { condition: !dv.deviationCriticality && this.dynamicLabel['DEVIATION_CRITICALITY'].isRemovable != 'Y', id: 'dv-criticality' },
      { condition: !dv.requestedAt, id: 'dv-requested-at' },
      { condition: this.businessAreaDropdown.length ? !this.dependency.selectedBusinessArea.length : !dv.coreBusinessAreaName, id: 'dv-business-area' },
      { condition: this.dynamicLabel?.['DEVIATION_OCCURED_ON']?.isRemovable !== 'Y' && !dv.deviationOccuredOn, id: 'dv-occurred-on' },
      { condition: this.dynamicLabel?.['DEVIATION_IDENTIFIED_ON']?.isRemovable !== 'Y' && !dv.deviationIdentifiedOn, id: 'dv-identified-on' },
      { condition: this.dynamicLabel['EXPECTED_TO_BE_STARTED_ON'].isRemovable != 'Y' && !dv.expectedToBeStartedOn, id: 'dv-ex-to-be-started-on' },
      { condition: !dv.expcetedClosureOn, id: 'dv-closure-on' },
      { condition: !dv.shortDescription, id: 'dv-short-description' },
      { condition: this.dynamicLabel['DV_DESCRIPTION'].isRemovable != 'Y' && !dv.dvDescription, id: 'dv-description' },
      { condition: this.dynamicLabel['REASON_FOR_DEVIATION_CODE'].isRemovable != 'Y' && !this.dependency.selectedReason.length, id: 'dv-reason' },
      { condition: this.visibleReasonForOthers && !dv.reasonForDeviationCode, id: 'dv-reason-if-others' }
    ];


    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setDvJson(dv);
    }
  }

  private validateIn(incident: IN): void {
    const validations = [
      { condition: !incident.inType, id: 'in-type' },
      { condition: !incident.deptCode && this.dynamicLabel['DEPT_NAME'].isRemovable != 'Y', id: 'in-dept' },
      { condition: !incident.requestedAt, id: 'in-requested-at' },
      { condition: this.businessAreaDropdown.length ? !this.dependency.selectedBusinessArea.length : !incident.coreBusinessAreaName, id: 'in-business-area' },
      { condition: !incident.reference, id: 'in-reference' },
      { condition: this.visibleReferenceIfOthers && !incident.referenceOthers, id: 'in-reference-others' },
      { condition: !incident.identifiedOn, id: 'in-identified-on' },
      { condition: this.dynamicLabel['EXPECTED_TO_BE_STARTED_ON'].isRemovable != 'Y' && !incident.expectedToBeStartedOn, id: 'in-ex-to-be-started-on' },
      { condition: !incident.expcetedClosureOn, id: 'in-closure-on' },
      { condition: incident.initiatedOnBehalfByOthers && !incident.initiatedOnBehalf, id: 'behalf-on-others' },
      { condition: !incident.impactType, id: 'in-impact-type' },
      { condition: !incident.impactDescription, id: 'in-impact-description' },
      { condition: !incident.shortDescription, id: 'in-short-description' },
      { condition: this.dynamicLabel['DESCRIPTION'].isRemovable != 'Y' && !incident.description, id: 'in-description' }
    ];

    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setInJson(incident);
    }
  }

  private validateQi(qi: QI): void {
    const validations = [
      { condition: !qi.qiType, id: 'qi-type' },
      { condition: !qi.deptCode && this.dynamicLabel['DEPT_NAME'].isRemovable != 'Y', id: 'qi-dept' },
      { condition: !qi.reference, id: 'qi-reference' },
      { condition: this.visibleReferenceIfOthers && !qi.referenceOthers, id: 'qi-reference-others' },
      { condition: !qi.requestedAt, id: 'qi-requested-at' },
      { condition: this.businessAreaDropdown.length ? !this.dependency.selectedBusinessArea.length : !qi.coreBusinessAreaName, id: 'qi-business-area' },
      { condition: !qi.evaluator, id: 'qi-evaluator' },
      { condition: !qi.identifiedOn, id: 'qi-identified-on' },
      { condition: this.dynamicLabel['EXPECTED_TO_BE_STARTED_ON'].isRemovable != 'Y' && !qi.expectedToBeStartedOn, id: 'qi-ex-to-be-started-on' },
      { condition: !qi.expcetedClosureOn, id: 'qi-closure-on' },
      { condition: !qi.shortDescription, id: 'qi-short-description' },
      { condition: !qi.impactType, id: 'qi-impact-type' },
      { condition: this.dynamicLabel['DESCRIPTION'].isRemovable != 'Y' && !qi.description, id: 'qi-description' },
      { condition: !qi.impactDescription, id: 'qi-impact-description' }
    ];

    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setQiJson(qi);
    }
  }

  private validateCc(cc: CC): void {
    const validations = [
      { condition: this.dynamicLabel['REPORTER_TITLE'].isRemovable != 'Y' && !cc.reporterTitle, id: 'title' },
      { condition: !cc.reporterFirstName, id: 'first-name' },
      { condition: this.dynamicLabel['REPORTER_LAST_NAME'].isRemovable != 'Y' && !cc.reporterLastName, id: 'last-name' },
      { condition: this.dynamicLabel['REPORTER_TYPE'].isRemovable != 'Y' && !cc.reporterType, id: 'reporter-type' },
      { condition: !cc.reporterComments, id: 'comments' }
    ];

    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setCcJson(cc);
    }
  }

  private validateOos(oos: OOS): void {
    const validations = [
      { condition: !oos.oostType, id: 'oost-type' },
      { condition: !oos.initiatingDeptCode && this.dynamicLabel['INITIATING_DEPT_NAME'].isRemovable != 'Y', id: 'initiating-dept' },
      { condition: !oos.oosDeptCode && this.dynamicLabel['OOS_DEPT_NAME'].isRemovable != 'Y', id: 'oos-dept' },
      { condition: !oos.owner, id: 'oos-owner' },
      { condition: !oos.dateOfOccurrence, id: 'oos-occurrence' },
      { condition: !oos.identifiedOn, id: 'oos-identified-on' },
      { condition: this.dynamicLabel['EXPECTED_TO_BE_STARTED_ON'].isRemovable != 'Y' && !oos.expectedToBeStartedOn, id: 'oos-ex-to-be-started-on' },
      { condition: !oos.expcetedClosureOn, id: 'oos-closure-on' }
    ];

    const invalidIds = validations.filter((validation) => validation.condition).map((validation) => validation.id);
    if (invalidIds.length > 0) {
      setTimeout(() => this.showSnackBar("Please enter all mandatory fields!", 'E'));
      this.fieldValidation(invalidIds);
    } else {
      this.setOosJson(oos);
    }
  }

  private setCrJson(cr: CR): void {
    const obj = {

      groupId: this.dependency.groupId,
      deptCode: cr.deptCode,
      owner: cr.owner,
      requestedAt: cr.requestedAt,
      coreBusinessAreaName: this.dependency.selectedBusinessArea.length ? this.dependency.selectedBusinessArea.join(',') : this.cr.coreBusinessAreaName,
      reasonForChange: cr.reasonForChange ? cr.reasonForChange : null,
      reasonForChangeCode: this.dependency.selectedReason.length ? this.dependency.selectedReason.join(',') : null,
      changeTypeCode: cr.changeTypeCode,
      shortDescription: cr.shortDescription,
      changeDescription: cr.changeDescription,
      changeJustification: cr.changeJustification,
      expcetedClosureOn: this.appService.dateZoneConvertion(new Date(cr.expcetedClosureOn!)),
      effectivenessCheckReqd: cr.effectivenessCheckReqd ? "Y" : "N",
      expectedToBeStartedOn: this.appService.dateZoneConvertion(new Date(cr.expectedToBeStartedOn!)),
      isRiskCalculationReqd: cr.isRiskCalculationReqd ? "Y" : "N"
    }

    this.createActivity(obj);
  }

  private setCapaJson(capa: CAPA): void {
    const obj = {

      groupId: this.dependency.groupId,
      deptCode: capa.deptCode,
      owner: capa.owner,
      capaType: capa.capaType,
      requestedAt: capa.requestedAt,
      coreBusinessAreaName: this.dependency.selectedBusinessArea.length ? this.dependency.selectedBusinessArea.join(',') : this.capa.coreBusinessAreaName,
      reasonForCapa: capa.reasonForCapa ? capa.reasonForCapa : null,
      reasonForCapaCode: this.dependency.selectedReason.length ? this.dependency.selectedReason.join(',') : null,
      expcetedClosureOn: this.appService.dateZoneConvertion(new Date(capa.expcetedClosureOn!)),
      expectedToBeStartedOn: this.appService.dateZoneConvertion(new Date(capa.expectedToBeStartedOn!)),
      effectivenessCheckReqd: capa.effectivenessCheckReqd ? "Y" : "N",
      isRiskCalculationReqd: capa.isRiskCalculationReqd ? "Y" : "N",
      shortDescription: capa.shortDescription,
      capaDescription: capa.capaDescription
    }

    this.createActivity(obj);
  }

  private setDvJson(dv: DV): void {
    const obj = {

      groupId: this.dependency.groupId,
      deptCode: dv.deptCode,
      owner: dv.owner,
      deviationType: dv.deviationType,
      deviationCriticality: dv.deviationCriticality,
      requestedAt: dv.requestedAt,
      coreBusinessAreaName: this.dependency.selectedBusinessArea.length ? this.dependency.selectedBusinessArea.join(',') : this.dv.coreBusinessAreaName,
      reasonForDeviation: dv.reasonForDeviation ? dv.reasonForDeviation : null,
      reasonForDeviationCode: this.dependency.selectedReason.length ? this.dependency.selectedReason.join(',') : null,
      deviationOccuredOn: this.appService.dateZoneConvertion(new Date(dv.deviationOccuredOn!)),
      deviationIdentifiedOn: this.appService.dateZoneConvertion(new Date(dv.deviationIdentifiedOn!)),
      expcetedClosureOn: this.appService.dateZoneConvertion(new Date(dv.expcetedClosureOn!)),
      expectedToBeStartedOn: this.appService.dateZoneConvertion(new Date(dv.expectedToBeStartedOn!)),
      effectivenessCheckReqd: dv.effectivenessCheckReqd ? "Y" : "N",
      isRiskCalculationReqd: dv.isRiskCalculationReqd ? "Y" : "N",
      shortDescription: dv.shortDescription,
      dvDescription: dv.dvDescription
    }

    this.createActivity(obj);
  }

  private setInJson(incident: IN): void {
    const obj = {
      groupId: this.dependency.groupId,
      inType: incident.inType,
      shortDescription: incident.shortDescription,
      description: incident.description,
      isOutsideOrganization: incident.isOutsideOrganization ? "Y" : "N",
      ...incident.initiatedOnBehalfByOthers && { initiatedOnBehalf: incident.initiatedOnBehalf },
      reference: incident.reference,
      deptCode: incident.deptCode,
      requestedAt: incident.requestedAt,
      coreBusinessAreaName: this.dependency.selectedBusinessArea.length ? this.dependency.selectedBusinessArea.join(',') : incident.coreBusinessAreaName,
      referenceOthers: incident.referenceOthers,
      identifiedOn: this.appService.dateZoneConvertion(new Date(incident.identifiedOn!)),
      expectedToBeStartedOn: this.appService.dateZoneConvertion(new Date(incident.expectedToBeStartedOn!)),
      expcetedClosureOn: this.appService.dateZoneConvertion(new Date(incident.expcetedClosureOn!)),
      impactType: incident.impactType,
      impactDescription: incident.impactDescription,
      isRiskCalculationReqd: incident.isRiskCalculationReqd ? "Y" : "N",
      effectivenessCheckReqd: incident.effectivenessCheckReqd ? "Y" : "N",
      raisedFrom: "Manage",
    }

    this.createActivity(obj);
  }

  private setQiJson(qi: QI): void {
    const obj = {

      groupId: this.dependency.groupId,
      shortDescription: qi.shortDescription,
      description: qi.description,
      qiType: qi.qiType,
      deptCode: qi.deptCode,
      requestedAt: qi.requestedAt,
      coreBusinessAreaName: this.dependency.selectedBusinessArea.length ? this.dependency.selectedBusinessArea.join(',') : qi.coreBusinessAreaName,
      reference: qi.reference,
      referenceOthers: qi.referenceOthers,
      identifiedOn: qi.identifiedOn ? this.appService.dateZoneConvertion(new Date(qi.identifiedOn)) : null,
      expectedToBeStartedOn: qi.expectedToBeStartedOn ? this.appService.dateZoneConvertion(new Date(qi.expectedToBeStartedOn)) : null,
      expcetedClosureOn: qi.expcetedClosureOn ? this.appService.dateZoneConvertion(new Date(qi.expcetedClosureOn)) : null,
      impactType: qi.impactType,
      impactDescription: qi.impactDescription,
      evaluator: qi.evaluator,
      effectivenessCheckReqd: qi.effectivenessCheckReqd ? "Y" : "N",
      isRiskCalculationReqd: qi.isRiskCalculationReqd ? "Y" : "N"
    }

    this.createActivity(obj);
  }

  private setCcJson(cc: CC): void {
    const obj = {
      groupId: this.dependency.groupId,
      reporterTitle: cc.reporterTitle,
      reporterFirstName: cc.reporterFirstName,
      reporterMiddleName: cc.reporterMiddleName,
      reporterLastName: cc.reporterLastName,
      reporterType: cc.reporterType,
      reporterDesignation: cc.reporterDesignation,
      emailAddress: cc.emailAddress,
      reporterContactNumber: cc.reporterContactNumber,
      reporterContactNumberOffice: cc.reporterContactNumberOffice,
      reporterAddress1: cc.reporterAddress1,
      reporterAddress2: cc.reporterAddress2,
      reporterFax: cc.reporterFax,
      reporterCity: cc.reporterCity,
      reporterState: cc.reporterState,
      reporterCountry: cc.reporterCountry,
      reporterZipcode: cc.reporterZipcode,
      reporterComments: cc.reporterComments,
      isRiskCalculationReqd: cc.isRiskCalculationReqd ? "Y" : "N",
      raisedFrom: "Manage",
    }

    this.createActivity(obj);
  }

  private setOosJson(oos: OOS): void {
    const obj = {

      groupId: this.dependency.groupId,
      initiatedBy: this.message.getUserId(),
      initiatingDeptCode: oos.initiatingDeptCode,
      oosDeptCode: oos.oosDeptCode,
      owner: oos.owner,
      identifiedOn: oos.identifiedOn ? this.appService.dateZoneConvertion(new Date(oos.identifiedOn)) : null,
      dateOfOccurrence: oos.dateOfOccurrence ? this.appService.dateZoneConvertion(new Date(oos.dateOfOccurrence)) : null,
      expectedToBeStartedOn: oos.expectedToBeStartedOn ? this.appService.dateZoneConvertion(new Date(oos.expectedToBeStartedOn)) : null,
      expcetedClosureOn: oos.expcetedClosureOn ? this.appService.dateZoneConvertion(new Date(oos.expcetedClosureOn)) : null,
      oostType: oos.oostType
    }

    this.createActivity(obj);
  }

  private createActivity(obj: any): void {
    let func: keyof AppService | undefined;

    func = this.getFunctionName(this.dependency.module);

    if (!func) {
      console.error('No function mapped for module: ', this.dependency.module);
      return;
    }

    this.isProcessing = true;
    this.appService[func](obj).subscribe({
      next: (Response: any) => {
        if (Response.resultType == "S") {
          this.dependency.createdActivityId = +Response.ID;
          if (this.flexibleComponent.flexibleFields.length) {
            this.flexibleComponent.save('master', +Response.ID);
          }
          this.addDependency();
        } else {
          this.isProcessing = false;
          this.showSnackBar(Response.result, Response.resultType);
        }
      },
      error: (error: any) => {
        this.isProcessing = false;
        throw error;
      }
    });
  }

  private getFunctionName(module: any): any {
    if (module === 'CR') {
      return 'createCr';
    } else if (module === 'CAPA') {
      return 'createCapa';
    } else if (module === 'DV') {
      return 'createDv';
    } else if (module === 'IN') {
      return 'raiseIncident';
    } else if (module === 'QI') {
      return 'createQmsQi';
    } else if (module === 'CC') {
      return 'raiseComplaint';
    } else if (module === 'OOS') {
      return 'createOos';
    } else {
      return '';
    }
  }

  public closeDependencyPopup(): void {
    this.visibleAddDependencyPopup.close();
  }

  private addDependency(): void {
    const obj: {} = {
      module: this.qualityService.moduleName,
      refNo: this.qualityService.activityId,
      dependentModule: this.dependency.module,
      dependentRefNo: this.dependency.createdActivityId,
    }

    this.appService.createQmsTaskDependencies(obj).subscribe({
      next: (Response: any) => {
        if (Response.resultType == "S") {
          this.visibleAddDependencyPopup.close();
          this.dependency = { ...RESET_DEPENDENCY };
          this.dependency.groupDetail = {};
          this.getCall();
          this.showSnackBar(Response.result, Response.resultType);
        } else {
          this.isProcessing = false;
          this.showSnackBar(Response.result, Response.resultType);
        }
      },
      error: (error: any) => {
        this.isProcessing = false;
        throw error;
      }
    });
  }

  public navigate(navigation: any, index: any): void {
    if (index == 0) {

      if (this.dependencyNavigationService.getStackSize() == 10) {
        setTimeout(() => { this.showSnackBar("Dear user you can navigate only 10 level deeper between dependent modules", 'E') }, 0);
        return;
      }

      this.isProcessing = true;
      this.coreNavigationService.navigationSource = 'DEPENDENCY';

      this.dependencyNavigationService.push({
        groupId: this.qualityService.selectedGroup,
        groupDetailId: this.qualityService.selectedGroupDetailId,
        groupDetails: this.qualityService.selectedGroupDetailData,
        moduleName: this.qualityService.moduleName,
        moduleFullName: this.qualityService.moduleFullName,
        activityId: this.qualityService.activityId
      })

      const refNo = navigation.DEPENDENT_REF_NO;

      if (navigation.DEPENDENT_MODULE == 'CR') {
        this.routeToModule({
          moduleName: 'CR',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'CR').codelistName,
          API: () => firstValueFrom(this.appService.getCrByCrNo(refNo)),
          extractGroupId: (cr: any) => cr.groupId,
          extractActivityId: (cr: any) => cr.crNo,
          targetRoute: 'dashboard/qmsCr'
        });

      } else if (navigation.DEPENDENT_MODULE == 'CAPA') {
        this.routeToModule({
          moduleName: 'CAPA',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'CAPA').codelistName,
          API: () => firstValueFrom(this.appService.getCapaVwDetailsByCapaNo(refNo)),
          extractGroupId: capa => capa.groupId,
          extractActivityId: capa => capa.capaNo,
          targetRoute: 'dashboard/capa'
        });

      } else if (navigation.DEPENDENT_MODULE == 'DV') {
        this.routeToModule({
          moduleName: 'DV',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'DV').codelistName,
          API: () => firstValueFrom(this.appService.getDvByDvNo(refNo)),
          extractGroupId: dv => dv.groupId,
          extractActivityId: dv => dv.dvNo,
          targetRoute: 'dashboard/dv'
        });

      } else if (navigation.DEPENDENT_MODULE == 'IN') {
        this.routeToModule({
          moduleName: 'IN',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'IN').codelistName,
          API: () => firstValueFrom(this.appService.getIncidentDetailsByInNo(refNo)),
          extractGroupId: incident => incident.groupId,
          extractActivityId: incident => incident.inNo,
          targetRoute: 'dashboard/in'
        });

      } else if (navigation.DEPENDENT_MODULE == 'QI') {
        this.routeToModule({
          moduleName: 'QI',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'QI').codelistName,
          API: () => firstValueFrom(this.appService.getQiVwDetailsByQiNo(refNo)),
          extractGroupId: qi => qi.groupId,
          extractActivityId: qi => qi.qiNo,
          targetRoute: 'dashboard/qi'
        });

      } else if (navigation.DEPENDENT_MODULE == 'CC') {
        this.routeToModule({
          moduleName: 'CC',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'CC').codelistName,
          API: () => firstValueFrom(this.appService.getCcDetailsByCcNo(refNo)),
          extractGroupId: cc => cc.groupId,
          extractActivityId: cc => cc.ccNo,
          targetRoute: 'dashboard/cc'
        });

      } else if (navigation.DEPENDENT_MODULE == 'OOS') {
        this.routeToModule({
          moduleName: 'OOS',
          moduleFullName: this.primaryModuleList.find((m: any) => m.codelistCode == 'OOS').codelistName,
          API: () => firstValueFrom(this.appService.getOosByOosNo(refNo)),
          extractGroupId: oos => oos[0].groupId,
          extractActivityId: oos => oos[0].oosNo,
          targetRoute: 'dashboard/qmsOOS'
        });

      }
    }
  }

  private async routeToModule(config: ModuleRouteConfig): Promise<void> {
    const currentModuleName = this.qualityService.moduleName;

    try {
      const data = await config.API();

      const groupId = config.extractGroupId(data);
      await this.getGroupDetails(groupId, config.moduleName);

      this.qualityService.moduleName = this.message.changeRequestCode = config.moduleName;
      this.qualityService.moduleFullName = config.moduleFullName;
      this.qualityService.selectedGroup = groupId;
      this.qualityService.activityId = this.message.activityId = config.extractActivityId(data);

    } catch (error) {
      this.isProcessing = false;
      console.error("Module routing failed:", error);
      setTimeout(() => this.showSnackBar("Failed to get module details", 'E'));
    } finally {
      this.qualityService.isTriggeredFromDependencies = true;

      const isSameModule = currentModuleName === config.moduleName;

      if (isSameModule) {
        this.dependencyNavigationService.refresh.emit();
      } else {
        this.router.navigate([config.targetRoute]);
      }
    }
  }

  private async getGroupDetails(groupId: number, moduleName: string): Promise<void> {
    try {
      const groupDetail: any = await firstValueFrom(this.appService.getQmsGroupDetailsByModule(groupId, moduleName));
      this.qualityService.selectedGroupDetailData = groupDetail;
      this.qualityService.selectedGroupDetailId = groupDetail.groupDetailId;
    } catch (error) {
      this.isProcessing = false;
      console.error("Failed group details:", error);
      throw error; // bubble up to route
    }
  }

  deleteDependency: any = {};
  public openDeletePopup(list: any): void {
    this.popup.title = 'Unmap Dependency'
    this.deleteDependency = list;
    this.deleteDependency.deleteReason = null;
    this.visibleDeleteDependencyPopup = this.dialog.open(this.deleteDependencyPopup, this.deleteDependencyPopupConfig);
  }

  public closeDeletePopup(): void {
    this.visibleDeleteDependencyPopup.close();
  }

  public deleteDependent(): void {
    if (this.deleteDependency.deleteReason) {
      this.isProcessing = true;

      const obj = {
        refNo: this.deleteDependency.REF_NO,
        dependentRefNo: this.deleteDependency.DEPENDENT_REF_NO,
        deleteReason: this.deleteDependency.deleteReason,
      }

      this.appService.deleteQmsTaskDependencies(obj).subscribe({
        next: (Response: any) => {
          if (Response.resultType == "S") {
            this.ngOnInit();
            this.visibleDeleteDependencyPopup.close();
            this.showSnackBar(Response.result, Response.resultType);
          } else {
            this.isProcessing = false;
            this.showSnackBar(Response.result, Response.resultType);
          }
        },
        error: (error) => {
          this.isProcessing = false;
          throw error;
        }
      });
    } else {
      const field = document.getElementById('unmap-reason') as HTMLInputElement;
      field.style.color = 'red';
    }
  }

  private showSnackBar(message: string, type: string): void {
    this.snackbarRef = this.snackBar.openFromComponent(SuccessSnackbarComponent, {
      duration: type === 'S' ? 5000 : undefined,
      data: {
        message,
        type: type === 'S' ? 'Success' : 'Error',
        buttonLabel: 'Ok',
        pageName: this.message.breadcrumbData.thirdLevel ?? this.message.breadcrumbData.secondLevel,
      },
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: type === 'S' ? 'Success' : 'Error',
    });

    this.snackbarRef.onAction().subscribe(() => {
      this.snackbarRef.dismiss();
    });
  }

  label: any = [];
  dynamicLabel: any = null;
  private getDynamicLabel(moduleCode: string): void {

    const tableMapping: Record<string, string> = {
      'CR': 'QMS_CR_GRID_VW', 'CAPA': 'QMS_CAPA_GRID_VW', 'DV': 'QMS_DV_GRID_VW', 'AU': 'QMS_AU_GRID_VW',
      'IN': 'QMS_IN_GRID_VW', 'QI': 'QMS_QI_GRID_VW', 'CC': 'QMS_CC_GRID_VW', 'OOS': 'QMS_OOS_GRID_VW'
    };

    this.isProcessing = true;
    const data = {
      tableName: tableMapping[moduleCode],
      moduleName: moduleCode,
      gridName: moduleCode,
      groupId: this.dependency.groupId
    };

    this.appService.getreorderGirdColumnList(data).subscribe({
      next: (labelResponse: any) => {
        this.label = labelResponse;
        this.dynamicLabel = this.label.reduce((acc: any, user: any) => {
          acc[`${user.columnName}`] = {
            fieldName: user.changedFieldName,
            isRemovable: user.isFieldRemovable,
            fieldNotes: user.fieldNotes
          };
          return acc;
        }, {});
        this.isProcessing = false;
      },
      error: (error: any) => {
        this.isProcessing = false;
        throw error;
      }
    });
  }

  searchText: any;
  searchValue: any;
  _temp: any = {};
  columnLists: any = {};
  grid: any = [];
  gridTemp: any;
  gridObj: any = {};
  makeStickyColumns: any;
  dataTypeList: any = [];
  visibleDataGrid: boolean = false;
  itemsPerPage = 5;
  currentPage = 1;
  totalPages = 0;
  maxSize = 3;
  collectionSize: number;
  perPage = 10;
  calculateTotalPages: number;
  showingResultsText = "Showing results";
  displayingClaimsText = "Displaying claims";
  rowText = "Rows";
  showingRowsText = "Showing rows";
  pager: any = {};
  pagedItems: any = [];
  error = '';

  getCall() {

    this.isProcessing = true;
    const primaryObj = {

      gridName: "DEPENDENCY",
    }

    this.appService.getDynamicGridHeaders(primaryObj).subscribe({
      next: (response: any) => {
        response.orderByClause = response.orderByClause ? response.orderByClause : "";
        this.makeStickyColumns = response.fixedFieldCnt;

        const secondaryObj: any = {

          gridName: "QMS_TASK_DEPENDENCIES_VW",
          tableOrViewName: response.tableOrViewName ?? "",
          columnsList: response.columnsList ?? "",
          fieldLabelsList: response.fieldLabelsList ?? "",
          invisibleColumn: response.invisibleColumn ?? "",
          orderByClause: response.orderByClause ?? "",
          fieldSizeList: response.fieldSizeList ?? "",
          columnsListarray: [],
          fieldColumnarray: [],
          invisibleColumnarray: [],
          fieldSizeListArray: [],
          manualEntryDataFields: response.manualEntryDataFields,
          refNoFromTaskDependencies: this.qualityService.activityId,
          clobColumnlist: response.closblist || "",
          blobColumnlist: response.bloblist || ""
        }

        this.gridObj = { ...secondaryObj, ...primaryObj };
        this.appService.getDynamicGridData(secondaryObj).subscribe({
          next: (response1: any) => {
            this.grid = response1;
            secondaryObj.columnsListarray = secondaryObj.columnsList.split(",");
            secondaryObj.fieldColumnarray = secondaryObj.fieldLabelsList.split(",");
            secondaryObj.invisibleColumnarray = secondaryObj.invisibleColumn.split(",");
            secondaryObj.fieldSizeListArray = secondaryObj.fieldSizeList.split(",");
            this.columnLists.columnsListarray = secondaryObj.columnsListarray;
            this.columnLists.fieldLabelsList = secondaryObj.fieldLabelsList;
            this.columnLists.fieldColumnarray = [];
            for (let p = 0; p < secondaryObj.fieldColumnarray.length; p++) {
              this.columnLists.fieldColumnarray.push({
                "fieldColumnarray": secondaryObj.fieldColumnarray[p],
                "width": secondaryObj.fieldSizeListArray[p] + 'px'
              })
            }
            this.columnLists.invisibleColumnarray = secondaryObj.invisibleColumnarray;
            this.columnLists.fieldSizeListArray = [];
            for (const element of secondaryObj.fieldSizeListArray) {
              this.columnLists.fieldSizeListArray.push(element + 'px');
            }
            for (let y = 0; y < this.columnLists.columnsListarray.length; y++) {
              for (const element of this.columnLists.invisibleColumnarray) {
                if (this.columnLists.columnsListarray[y] == element) {
                  this.columnLists.columnsListarray.splice(y, 1);
                  this.columnLists.fieldColumnarray.splice(y, 1);
                  this.columnLists.fieldSizeListArray.splice(y, 1);
                }
              }
            }
            let tableData = {
              tablename: secondaryObj.tableOrViewName
            };
            this.columnLists.columnsListarray1 = [];
            this.appService.tableFieldsDataType(tableData).subscribe((response2: any) => {
              this.dataTypeList = response2;
              for (let m = 0; m < this.columnLists.columnsListarray.length; m++) {
                this.columnLists.columnsListarray1.push({});
                this.columnLists.columnsListarray1[m].columnsListarray = this.columnLists.columnsListarray[m];
                this.columnLists.columnsListarray1[m].dataType = "textbox";
                this.columnLists.columnsListarray1[m].width = this.columnLists.fieldSizeListArray;
                for (const element of this.dataTypeList) {
                  if (element.columnName == this.columnLists.columnsListarray[m]) {
                    this.columnLists.columnsListarray1[m].dataType = element.fieldType;
                  }
                }
              }
              for (const rowData of this.grid) {
                for (const element of this.dataTypeList) {
                  if (element.fieldType === 'CHECKBOX') {
                    rowData[element.columnName] = rowData[element.columnName] === 'Y';
                  }
                }
              }
              this.calculateTotalPages = this.collectionSize / this.perPage;
              this.pager.totalPages = undefined;
              this.setPage(1, 0);
            });
            this.message.gridList = this.grid;
            this.collectionSize = this.grid.length;
            this.totalPages = Math.ceil(this.grid.length / this.itemsPerPage);
            this.isProcessing = false;
          }, error: (error) => {
            this.isProcessing = false;
            throw error;
          }
        });
        this.isProcessing = false;
      }, error: (error) => {
        this.isProcessing = false;
        throw error;
      }
    });
  }

  setPage(page: number, targetPage: number) {
    if (page < 1 || page > this.pager.totalPages) {
      return;
    }
    if (Number.isNaN(page)) {
      page = 1;
    }
    this.pager = this.paginationService.getPager(this.grid.length, page, this.perPage, targetPage);
    this.pagedItems = this.grid.slice(this.pager.startIndex, this.pager.endIndex + 1);
  }

  setPagedOrSearch(page: number, targetPage: number) {
    if (Number.isNaN(page)) {
      page = 1;
    }

    if (this.gridTemp.length > 0) {
      this.pager = this.paginationService.getPager(this.gridTemp.length, page, this.perPage, targetPage);
      this.pagedItems = this.gridTemp.slice(this.pager.startIndex, this.pager.endIndex + 1);
    } else {
      this.pagedItems = [];
    }
  }

  paginationCall(value: number) {
    this.perPage = value;
    this.setPage(this.pager.currentPage, 0);
  }

  applyFilter3(column: string, event: Event): void {
    const filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (filter === '') {
      this.pagedItems = this.grid.slice(this.pager.startIndex, this.pager.endIndex + 1);
    } else {
      this.pagedItems = this.grid.filter((item: any) =>
        item[column]?.toLowerCase().includes(filter)
      );
    }
  }

  searchValue2: any;
  applyFilter2(event: Event) {
    const filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (filter === '') {
      this.gridTemp = this.grid;
      this.pager = this.paginationService.getPager(this.gridTemp.length, 1, this.perPage, 0);
      this.pagedItems = this.gridTemp.slice(this.pager.startIndex, this.pager.endIndex + 1);
    } else {
      if (this.grid == undefined) {
        return this.grid;
      }
      if (!this.grid.length) {
        return this.grid;
      }
      if (filter) {
        this.gridTemp = this.grid.filter(function (item: any) {
          return JSON.stringify(item).toLowerCase().includes(filter);
        });
      } else {
        this.gridTemp = this.grid.filter(function (item: any) {
          return JSON.stringify(item).toLowerCase();
        });
      }
      this.setPagedOrSearch(1, 0);
    }
  }

  sortedColumn: string = '';
  sortedDisplayColumn: string = '';
  sortDirection: string = 'asc';
  sortColumn(column: string, list: any) {
    this.isProcessing = true;
    if (column === this.sortedColumn) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortedColumn = column;
      this.sortedDisplayColumn = list.fieldColumnarray;
      this.sortDirection = 'asc';
    }
    list.showsort = this.sortDirection;

    this.grid.sort((a: { [x: string]: any; }, b: { [x: string]: any; }) => {
      const key = column;
      const valueA = a[key];
      const valueB = b[key];

      if (valueA < valueB) {
        return this.sortDirection === 'asc' ? -1 : 1;
      } else if (valueA > valueB) {
        return this.sortDirection === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });
    this.setPage(1, 0);
  }
  counts: any;
  cssFunction(ix: number) {
    let elements1 = this.elem.nativeElement.querySelectorAll('.sticky-dynamic-col1');
    let index1 = 1;
    let headerWidth = 0;
    elements1.forEach((header: any, key: any) => {
      for (let i = 1; i <= this.makeStickyColumns; i++) {
        if (index1 == i) {
          header.style.position = 'sticky';
          header.style.background = 'white';
          if (index1 > 1) {
            headerWidth += 200;
            header.style.left = headerWidth + 'px';
          } else {
            headerWidth = 60;
            header.style.left = headerWidth + 'px';
          }
        }
      }
      index1++;
    })

    let elements2 = this.elem.nativeElement.querySelectorAll('.sticky-dynamic-row1');
    let index2 = 1;
    let lengthOfColumns = this.columnLists.columnsListarray1.length;
    let colWidth = 0;
    let p = 1;
    elements2.forEach((column: any, key: any) => {
      for (let j = 1; j <= this.makeStickyColumns; j++) {
        if (index2 == j) {
          column.style.position = 'sticky';
          p = p + 1;
          let keyValue = p / 2;
          column.style.background = keyValue == Math.floor(keyValue) ? 'white' : '';
          column.style.textDecoration = "underline";
          column.style.color = "blue";
          column.style.cursor = "pointer";
          if (index2 > 1) {
            colWidth += 200;
            let previousColumn = elements2[key - 1];
            console.log(previousColumn.style.width);
            column.style.left = colWidth + 'px';
          } else {
            colWidth = 60;
            column.style.left = colWidth + 'px';
          }
          column.addEventListener('click', this.onClick.bind(this));
        }
      }
      if (index2 == (lengthOfColumns - 1)) {
        index2 = 0;
      } else {
        index2++;
      }
    })
  }

  onClick(event: any) {

  }

  parentCheckBox: boolean = false;
  childCheckBox: boolean = false;
  dataGridParentCheckBoxChecked(e: any) {
    if (e.target.checked) {
      this.childCheckBox = true;
      for (const element of this.grid) {
        element.childCheckBox = true;
      }
    } else {
      this.childCheckBox = false;
      for (const element of this.grid) {
        element.childCheckBox = false;
      }
    }
  }

  previousSelectedValue: any;
  dataGridChildCheckBoxChecked(e: any, list: any, idx: any) {
    if (e.target.checked) {
      if (this.previousSelectedValue != undefined) {
        this.grid[this.previousSelectedValue].childCheckBox = false;
      }
      list.childCheckBox = true;
    } else {
      list.childCheckBox = false;
    }
    this.previousSelectedValue = idx;
  }

  refreshGrid() {
    this.isProcessing = true;
    this.getCall();
  }


  @ViewChild(GridReorderComponent) gridReorderComponent!: GridReorderComponent;
  openReorder() {
    this.gridReorderComponent.reordercall('DEPENDENCY', 'QMS_TASK_DEPENDENCIES_VW', this.qualityService.moduleName, 0)
  }

  onChildValueEmitted() {
    this.getCall();
  };

  htmlContent: any;
  @ViewChild(EditorComponent) editorComponent!: EditorComponent;
  getClob(list: any, columnName: any, popupColumnName: any) {
    this.isProcessing = true;
    let docObj = {
      primeIdentifier: list.PRIM_IDENTIFIER,
      tableView: list.tablevwname,
      columnName: columnName
    }
    this.htmlContent = "";
    this.appService.getClobToViewAudit(docObj).subscribe({
      next: (Response: any) => {
        this.htmlContent = Response.result;
        this.editorComponent.openEditor(this.htmlContent, popupColumnName);
      }, error: (error) => {
        this.isProcessing = false;
        throw error;
      }
    });
    this.isProcessing = false;
  }

  public focusEmpty() {
    console.log();
  }

  public getAuditPrivilege(): boolean {
    const status = this.qualityService.activity.status;

    return (this.auService.isAuditor && (
      status == 'Received Q&A Response' || status == 'In Progress/Received Q&A Response' ||
      status == 'Inprogress' || status == 'Peer Review Rejected' ||
      status == 'Peer Reviewer Requested More Info.' || status == 'Closure Review Rejected' ||
      status == 'Closure Reviewer Requested More Info.' || status == 'Closure Approval Rejected' ||
      status == 'Closure Approver Requested More Info.' || status == 'Auditee Requested More Info.'
      || status == 'Auditee Accepted' || status == 'In Progress' || status == 'Findings In Progress')) ||
      (this.auService.isAuditee && (status == 'Findings In Progress' || status == 'Auditee Requested More Info.' || status == 'Pending Auditee Review'))
  }

  idMapping: Record<string, string> = { 'CR': this?.qualityService?.activity?.crId, 'CAPA': this.qualityService.activity.capaId, 'DV': this.qualityService.activity.dvId, 'AU': this.qualityService.activity.auId, 'IN': this.qualityService.activity.inId, 'QI': this.qualityService.activity.qiId, 'CC': this.qualityService.activity.ccId };

  @ViewChild(ExcelPdfComponent) excelPdfComponent!: ExcelPdfComponent;
  excelPdf: any = {};

  openReport(reportType: string) {
    this.excelPdf.reportObj = { ...this.gridObj, excludeGroupId: true };
    this.excelPdf.reportType = 'Dynamic';
    this.excelPdf.reportName = this.qualityService.moduleName + ' Dependency Report [' + this.idMapping[this.qualityService.moduleName] + ']';
    this.excelPdf.dropDown = this.columnLists?.fieldColumnarray?.map((item: any) => item.fieldColumnarray);
    this.excelPdfComponent.openReportPopup(reportType);
  }

  @ViewChild(DynamicAuditTrailComponent) dynamicAuditTrailComponent!: DynamicAuditTrailComponent;
  auditTrail: any = {};

  openAuditTrail(viewType: any, action: any) {
    this.auditTrail.refNo = viewType == 'overAll' ? null : action.DEPENDENT_REF_NO;
    this.auditTrail.viewType = viewType;
    this.auditTrail.activityRefNo = this.message.activityId;
    this.auditTrail.gridName = 'DEPENDENCY';
    this.auditTrail.report_And_Popup_Name = this.qualityService.moduleName + ' Dependency Audit Trail [' + this.idMapping[this.qualityService.moduleName] + ']';
    this.dynamicAuditTrailComponent.triggerTrail(action);
  }

}
